import React, { useState, useEffect, useRef } from "react";
import { Toaster, toast } from "react-hot-toast";
import { Loader, useWindowSize, Wrapper } from "../common";
import { toI18N } from "../../i18n";
import fetchApi from "../../services/FetchApi";
import * as GoogleAnalyticsService from "../../services/GoogleAnalyticsService";
import CommunityBrandHeader from "./CommunityBrandHeader";

const formatLocalDateYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export type BirthdayVerificationProps = {
  userId: string;
  email: string;
  jwtToken: string;
  refreshToken: string;
  refreshTokenV2?: string;
  phoneNumber?: string;
  coverImage?: string;
  backgroundColor?: string;
  logo?: string;
  title?: string;
  handleBack: () => void;
  handleBirthdayVerified: (userId: string, refreshToken: string, refreshTokenV2?: string) => void;
  loading: boolean;
  // Pending-signup mode: the account doesn't exist yet. Submitting exchanges
  // this token + birthday for the real account via /complete-signup, and the
  // logged-in payload is delivered through handleSignupCompleted instead of
  // handleBirthdayVerified.
  signupToken?: string;
  handleSignupCompleted?: (appUser: any) => void | Promise<void>;
  // The parent pauses its own challenge poll between these two, so nothing can
  // resolve the flow underneath an in-flight completion.
  onSignupSubmitted?: () => void;
  onSignupSubmitFailed?: () => void;
  // Analytics only: the community and invitation this gate belongs to, so
  // the age_gate rows can be joined to the rest of the funnel.
  selectId?: string | null;
  groupId?: string | null;
  linkId?: string | null;
  journeyId?: string | null;
};

export default function BirthdayVerification(props: BirthdayVerificationProps) {
  // Deliberately starts empty: a pre-filled passing date lets users clear the
  // age gate without ever asserting their age (compliance requirement).
  const [birthday, setBirthday] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [inIframe, setInIframe] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const size = useWindowSize();

  useEffect(() => {
    setInIframe(window.top !== window.self);
  }, []);

  // Analytics only. One `shown` row per gate, then one row per refusal:
  // `under_16` from the client check, `server_rejected` when the backend
  // repeats the check and refuses. A person who passes produces one row;
  // one who is refused produces two. `origin` is added by the wrapper.
  const gateParams = {
    group_id: props.groupId ?? undefined,
    select_id: props.selectId ?? undefined,
    link_id: props.linkId ?? undefined,
    journey_id: props.journeyId ?? undefined,
  };
  const shownReportedRef = useRef(false);
  useEffect(() => {
    if (shownReportedRef.current) return;
    shownReportedRef.current = true;
    GoogleAnalyticsService.logEvent("age_gate", { outcome: "shown", ...gateParams });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateAge = (dateString: string): boolean => {
    const birthDate = new Date(dateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // Adjust age if birthday hasn't occurred this year yet
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 16;
  };

  const handleContinue = async () => {
    if (!birthday) {
      setError(toI18N("lp_login_birthday_required"));
      return;
    }

    if (!validateAge(birthday)) {
      GoogleAnalyticsService.logEvent("age_gate", { outcome: "under_16", ...gateParams });
      setError(toI18N("lp_login_birthday_age_restriction"));
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      if (props.signupToken) {
        props.onSignupSubmitted?.();
        // Pending signup: the account is only created now, after the backend
        // repeats the age check. The response carries the session tokens.
        const appUser = await fetchApi.completeSignup(props.signupToken, birthday);
        await props.handleSignupCompleted?.(appUser);
        return;
      }

      // Existing account with a missing birthday: plain field update
      await fetchApi.updateUserBirthday(birthday, props.jwtToken);

      // Birthday verification successful, proceed with authentication
      props.handleBirthdayVerified(props.userId, props.refreshToken, props.refreshTokenV2);
    } catch (e: any) {
      if (e?.serverMessage === "age_requirement_not_met") {
        GoogleAnalyticsService.logEvent("age_gate", {
          outcome: "server_rejected",
          ...gateParams,
        });
        setError(toI18N("lp_login_birthday_age_restriction"));
      } else if (
        e?.serverMessage === "signup token expired" ||
        e?.serverMessage === "invalid signup token"
      ) {
        toast.error(toI18N("lp_login_signup_session_expired"));
        props.handleBack();
      } else {
        toast.error(toI18N("lp_login_birthday_update_fail_toast"));
      }
      // Hand the flow back, or a failure here leaves the other device unable to
      // finish this tab for them.
      props.onSignupSubmitFailed?.();
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter" && !isLoading && birthday) {
      handleContinue();
    }
  };

  return (
    <Wrapper InIframe={inIframe} isMobile={size.width < 481} coverImage={props.coverImage} backgroundColor={props.backgroundColor}>
      <div className="p-8" aria-busy={props.loading || isLoading}>
        {/* Nav row: back control left, community lockup centered
            (symmetric flex-1 spacers keep it optically centered). */}
        <div className="mb-4 flex items-center">
          <div className="flex flex-1 justify-start">
            <button
              onClick={props.handleBack}
              className="p-2 bg-neutral-1 rounded-full hover:bg-neutral-11 text-primary-3"
              disabled={isLoading}
              aria-label="Go back"
            >
              <img
                src={"/back_button.svg"}
                alt=""
                aria-hidden="true"
                className={`w-15 h-15`}
              />
            </button>
          </div>
          <CommunityBrandHeader logo={props.logo} title={props.title} />
          <div className="flex-1" aria-hidden="true" />
        </div>
        <div className="flex flex-col items-center">
          <img
            src={"/birthday_icon.svg"}
            alt="Birthday verification"
            className={`mb-3 w-20 h-20`}
          />
          <h1
            className="mb-5 text-[24px] pb-8 font-bold text-center"
            style={{
              fontSize: "24px",
              fontWeight: "700",
              lineHeight: "29px",
              letterSpacing: "0em",
            }}
          >
            {toI18N("lp_login_birthday_title")}
          </h1>
          <h2
            className="mb-5 pb-8"
            style={{
              fontSize: "16px",
              fontWeight: "300",
              lineHeight: "16px",
              letterSpacing: "0em",
              textAlign: "center",
            }}
          >
            {toI18N("lp_login_birthday_description")}
          </h2>

          <div className="w-full mb-3">
            <label htmlFor="birthday" className="sr-only">
              {toI18N("general_birthday")}
            </label>
            <input
              type="date"
              id="birthday"
              placeholder={toI18N("general_birthday")}
              className={`modern-datepicker w-full border rounded bg-primary-4 ${
                error ? "border-red-500" : "border-neutral-11"
              }`}
              style={{ borderRadius: "12px" }}
              value={birthday}
              onChange={(e) => {
                // The max attribute only constrains the picker; typed years
                // can exceed it, so clamp future dates back to today.
                const value = e.target.value;
                const max = formatLocalDateYYYYMMDD(new Date());
                setBirthday(value > max ? max : value);
                if (error) setError("");
              }}
              onKeyDown={handleKeyDown}
              max={formatLocalDateYYYYMMDD(new Date())}
              disabled={isLoading}
              aria-describedby={error ? "birthday-error" : undefined}
              aria-invalid={error ? "true" : undefined}
            />
            {error && (
              <p id="birthday-error" className="mt-2 text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
          </div>

          <button
            onClick={handleContinue}
            disabled={isLoading || !birthday}
            className={`w-full mb-3 p-3 border border-neutral-11 text-neutral-1 ${
              isLoading || !birthday
                ? "bg-neutral-7 cursor-not-allowed"
                : "bg-primary-1 hover:bg-primary-2"
            }`}
            style={{
              borderRadius: "24px",
              fontSize: "16px",
              fontWeight: "700",
              lineHeight: "18px",
              letterSpacing: "0em",
              textAlign: "center",
            }}
          >
            {toI18N("general_continue")}
          </button>

          <div className="mt-2 text-xs text-center text-gray-500">
            <p>
              {toI18N("lp_login_birthday_privacy_note")}
            </p>
          </div>
        </div>

        <Toaster position="bottom-center" />
        {(props.loading || isLoading) && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-primary-4 opacity-50" />
            <div className="absolute inset-0">
              <Loader />
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}
